'use client';

import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import { FC, useRef } from 'react';

import Image from '@/components/Image';
import { TrustedBySectionDto } from '@/components/ServicesLobbyPage/TrustedBySection/types';
import StrapiRichContent from '@/components/StrapiRichContent';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

interface Props {
	data: TrustedBySectionDto;
}

const TrustedBySection: FC<Props> = ({ data }) => {
	const sectionRef = useRef<HTMLElement>(null);
	const mainTextRef = useRef<HTMLHeadingElement>(null);

	useGSAP(
		() => {
			const wrapper = sectionRef.current;
			const mainTextEl = mainTextRef.current;
			const mainTextLines = mainTextEl?.querySelectorAll('p');
			const logos = wrapper?.querySelectorAll(`.${css.logo}`);

			if (!wrapper || !mainTextEl) return;

			const textTargets =
				mainTextLines && mainTextLines.length ? Array.from(mainTextLines) : [mainTextEl];

			gsap.set(textTargets, {
				opacity: 0,
				y: 40,
				filter: 'blur(10px)'
			});

			gsap.to(textTargets, {
				opacity: 1,
				y: 0,
				filter: 'blur(0px)',
				duration: 1.1,
				ease: 'power3.out',
				stagger: textTargets.length > 1 ? 0.15 : 0,
				scrollTrigger: {
					trigger: mainTextEl,
					start: 'top 80%',
					toggleActions: 'play none none reverse'
				}
			});

			if (logos?.length)
				gsap.from(logos, {
					y: 60,
					opacity: 0,
					stagger: 0.12,
					duration: 1,
					ease: 'power3.out',
					scrollTrigger: {
						trigger: wrapper.querySelector(`.${css.logos}`) ?? wrapper,
						start: 'top 75%',
						toggleActions: 'play none none reverse'
					}
				});
		},
		{ scope: sectionRef }
	);

	return (
		<section ref={sectionRef} className={clsx('container', css.section)}>
			<h2 ref={mainTextRef} className={css.mainText}>
				<StrapiRichContent content={data.mainText} />
			</h2>

			<div className={css.logos}>
				{data.logos.map(({ image, id }) => (
					<Image
						key={id}
						className={css.logo}
						width={image.width}
						height={image.height}
						src={image.url}
						preload={false}
						alt={image.alternativeText ?? ''}
					/>
				))}
			</div>
		</section>
	);
};

export default TrustedBySection;
